// ---------------------------------------------------------------------------
// Gated URL state — a pass-through wrapper that gates `subscribe` notifications
// ---------------------------------------------------------------------------
//
// data-grid subscribes to its `urlState` itself and auto-loads on every change.
// When a data-grid is mounted-but-hidden (e.g. an inactive view), we don't want
// it to fetch on URL changes. This wrapper forwards the real urlState's
// notifications to its subscribers ONLY while `active`. Reads and writes always
// pass straight through (the gated grid's own page/sort writes still hit the
// real URL). `flush()` re-emits the current params once (used to reload a hidden
// grid exactly when it becomes active).
//
// Pure wrapper over the existing url-state surface — adds `setActive`/`flush`/
// `destroy`. data-grid consumes it unchanged (it just sees "a urlState").

/**
 * @typedef {import('./url-state.js').UrlStateListener} UrlStateListener
 * @typedef {{
 *   get: (key: string) => string | string[] | undefined,
 *   set: (key: string, value: string | string[]) => void,
 *   setMany: (updates: Record<string, string | string[]>) => void,
 *   replace: (params: Record<string, string | string[]>) => void,
 *   subscribe: (listener: UrlStateListener) => () => void,
 *   toQueryObject: () => Record<string, string | string[]>,
 * }} UrlStateLike
 */

/**
 * @typedef {UrlStateLike & {
 *   setActive: (active: boolean) => void,
 *   flush: () => void,
 *   destroy: () => void,
 * }} GatedUrlState
 */

/**
 * Wrap a real urlState so `subscribe` notifications are gated by `active`.
 *
 * @param {UrlStateLike} real
 * @param {{ active?: boolean }} [options]
 * @returns {GatedUrlState}
 */
export function createGatedUrlState(real, options = {}) {
  let isActive = options.active === true;

  /** @type {Set<UrlStateListener>} */
  const listeners = new Set();

  // Exactly ONE subscription to the real urlState, regardless of how many gated
  // subscribers register. Forwarded only while active.
  const unsubscribeReal = real.subscribe((params) => {
    if (!isActive) return;
    for (const listener of [...listeners]) listener(params);
  });

  return {
    // --- pass-through reads/writes ---
    get: (key) => real.get(key),
    set: (key, value) => real.set(key, value),
    setMany: (updates) => real.setMany(updates),
    replace: (params) => real.replace(params),
    toQueryObject: () => real.toQueryObject(),

    // --- gated subscribe (does NOT eagerly invoke the listener) ---
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },

    setActive(active) {
      isActive = active === true;
    },

    /** Re-emit the current params to gated subscribers, once, only when active. */
    flush() {
      if (!isActive) return;
      const params = real.toQueryObject();
      for (const listener of [...listeners]) listener(params);
    },

    destroy() {
      listeners.clear();
      unsubscribeReal?.();
    },
  };
}
